/**
 * Show who the answer is about — whether or not the model remembered to.
 *
 * "Who is my father?" asked on the family tree should end with the tree zoomed
 * to him. The model is asked to call `show` for exactly that, and a small, fast
 * model answering in a hurry often answers correctly and forgets: the right
 * words, and a screen that did not move. A behaviour that matters this much
 * cannot depend on a model remembering an instruction.
 *
 * So the app checks for itself. Everybody the model looked up while answering
 * left their id in the lookup results; whichever of them the answer names first
 * is who the answer is about, and that person is shown on the screen they are
 * on. Names are matched as whole words, in both languages, by full name, given
 * name, nickname or a previous name — never by family name alone, which half
 * the tree shares.
 */
import { normalize } from '../../domain/names';
import type { PersonWithNames } from '../../domain/types';

const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/** The ids a lookup mentioned, first seen first. */
export function idsIn(text: string): string[] {
  return [...new Set((text.match(UUID) ?? []).map((id) => id.toLowerCase()))];
}

/** Every way an answer might name somebody. */
export function namesOf(p: PersonWithNames): string[] {
  const out = new Set<string>();
  for (const n of p.names) {
    for (const s of [n.display, `${n.given} ${n.family}`, n.nickname, n.given, ...(n.previousNames ?? [])]) {
      const v = normalize(s ?? '');
      if (v.length >= 2) out.add(v);
    }
  }
  return [...out];
}

const isLetter = (ch: string | undefined) => !!ch && /[\p{L}\p{N}]/u.test(ch);
/** Hebrew fixes "and", "the", "to", "of", "in", "from", "as" to the front of the word: ולדוד, של דוד, לדוד. */
const HEBREW_PREFIX = new Set(['ו', 'ה', 'ב', 'ל', 'מ', 'ש', 'כ']);

/** Where a name first appears as whole words, or -1. A Hebrew one-letter prefix still counts. */
export function wordIndex(hay: string, name: string): number {
  for (let at = hay.indexOf(name); at >= 0; at = hay.indexOf(name, at + 1)) {
    if (isLetter(hay[at + name.length])) continue;
    const before = hay[at - 1];
    if (!isLetter(before)) return at;
    if (HEBREW_PREFIX.has(before) && !isLetter(hay[at - 2])) return at;
  }
  return -1;
}

/**
 * The person the text names first.
 *
 * At the same position the longer name wins ("Miriam Edry" over "Miriam"). The
 * person asking is only chosen when nobody else is named — "Sarah, your father
 * is David" is about David.
 */
export function firstNamed(text: string, people: PersonWithNames[], meId?: string): string | null {
  const hay = normalize(text);
  let best: { id: string; at: number; len: number } | null = null;
  let me: { id: string; at: number } | null = null;
  for (const p of people) {
    for (const name of namesOf(p)) {
      const at = wordIndex(hay, name);
      if (at < 0) continue;
      if (p.id === meId) {
        if (!me || at < me.at) me = { id: p.id, at };
        continue;
      }
      if (!best || at < best.at || (at === best.at && name.length > best.len)) best = { id: p.id, at, len: name.length };
    }
  }
  return best?.id ?? me?.id ?? null;
}

/**
 * A family member named in the text when the lookups carried no ids to go on —
 * "who lives in Haifa?" is answered from a list of names. Only a name that
 * belongs to exactly one person counts: two Davids in the family means "David"
 * shows nobody rather than the wrong one.
 */
export function firstUniquelyNamed(text: string, family: PersonWithNames[], meId?: string): string | null {
  const owners = new Map<string, Set<string>>();
  for (const p of family) for (const name of namesOf(p)) owners.set(name, (owners.get(name) ?? new Set()).add(p.id));
  const hay = normalize(text);
  let best: { id: string; at: number; len: number } | null = null;
  for (const p of family) {
    if (p.id === meId) continue;
    for (const name of namesOf(p)) {
      // A name two people share names nobody for certain.
      if (owners.get(name)?.size !== 1) continue;
      const at = wordIndex(hay, name);
      if (at >= 0 && (!best || at < best.at || (at === best.at && name.length > best.len))) best = { id: p.id, at, len: name.length };
    }
  }
  return best?.id ?? null;
}

/** The story whose title the text names first — titles are long enough that a plain match is safe. */
export function firstTitled(text: string, stories: Array<{ id: string; title: string }>): string | null {
  const hay = normalize(text);
  let best: { id: string; at: number } | null = null;
  for (const s of stories) {
    const title = normalize(s.title);
    if (title.length < 6) continue;
    const at = hay.indexOf(title);
    if (at >= 0 && (!best || at < best.at)) best = { id: s.id, at };
  }
  return best?.id ?? null;
}
